/**
 * Entrada da Centuria — o fluxo de boas-vindas de uma conversa nova e os
 * atalhos (chips) acima do campo de mensagem. Espelha `flows/domain/entry.py`:
 * até 6 atalhos, texto de 1 a 40 caracteres, um fluxo por atalho.
 */
import type { EntrySettings, EntrySettingsBody, FlowSummary, Shortcut, ShortcutIcon } from './types';

export const MAX_SHORTCUTS = 6;
export const SHORTCUT_LABEL_MAX = 40;

export const SHORTCUT_ICONS: { value: ShortcutIcon; label: string }[] = [
  { value: 'sparkle', label: 'Brilho' },
  { value: 'chat', label: 'Conversa' },
  { value: 'question', label: 'Dúvida' },
  { value: 'book', label: 'Livro' },
  { value: 'video', label: 'Vídeo' },
  { value: 'calendar', label: 'Agenda' },
  { value: 'handshake', label: 'Aperto de mão' },
  { value: 'phone', label: 'Telefone' },
  { value: 'lightbulb', label: 'Ideia' },
  { value: 'package', label: 'Produto' },
  { value: 'star', label: 'Estrela' },
  { value: 'user', label: 'Pessoa' },
];

export interface EntryForm {
  welcomeFlowId: string;
  shortcuts: Shortcut[];
}

export function toEntryForm(s: EntrySettings): EntryForm {
  return {
    welcomeFlowId: s.welcome_flow_id ?? '',
    shortcuts: s.shortcuts.map((x) => ({ flow_id: x.flow_id, label: x.label, icon: x.icon })),
  };
}

export function toEntryBody(form: EntryForm): EntrySettingsBody {
  return {
    welcome_flow_id: form.welcomeFlowId || null,
    shortcuts: form.shortcuts.map((s) => ({ ...s, label: s.label.trim().replace(/\s+/g, ' ') })),
  };
}

/** Por atalho, o que impede salvar (índice → mensagem). Vazio: pode salvar. */
export function shortcutProblems(form: EntryForm): Map<number, string> {
  const problems = new Map<number, string>();
  const seen = new Set<string>();
  form.shortcuts.forEach((s, i) => {
    const label = s.label.trim();
    if (!s.flow_id) problems.set(i, 'Escolha o fluxo do atalho.');
    else if (seen.has(s.flow_id)) problems.set(i, 'Este fluxo já está em outro atalho.');
    else if (!label) problems.set(i, 'Escreva o texto do atalho.');
    else if (label.length > SHORTCUT_LABEL_MAX)
      problems.set(i, `No máximo ${SHORTCUT_LABEL_MAX} caracteres.`);
    if (s.flow_id) seen.add(s.flow_id);
  });
  return problems;
}

/** Fluxos escolhidos que não estão no ar — o portal os esconde. */
export function unpublishedChoices(form: EntryForm, flows: readonly FlowSummary[]): Set<string> {
  const live = new Set(flows.filter((f) => f.live_version !== null).map((f) => f.id));
  const chosen = [form.welcomeFlowId, ...form.shortcuts.map((s) => s.flow_id)].filter(Boolean);
  return new Set(chosen.filter((id) => !live.has(id)));
}
